import React, { useEffect, useState } from 'react';
import { Node } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';

/* Docs — an INLINE sub-page: a child document embedded as a block in its
 * parent's body, the way Notion nests pages. Distinct from the list-view tree,
 * which shows the same parentId relationship but only in the sidebar sense.
 *
 * TITLE IS RESOLVED LIVE, not snapshotted. The node stores only `docId` (plus a
 * `label` fallback for when the doc can't be resolved); the title is looked up
 * at render, so renaming a child updates every reference to it. Same reasoning
 * as the entity-mention chips.
 *
 * DELETING THE BLOCK DOES NOT DELETE THE DOCUMENT. It removes the inline
 * reference only — the page still exists in the tree and is reachable from the
 * list. Silently destroying a document by deleting a line of text is a far worse
 * failure than leaving an orphan one click away.
 *
 * Handlers arrive through a stable ref (see CyvEditor) so that changing
 * callbacks never re-create the editor mid-edit.
 */

function ChildPageView({ node, extension, editor, getPos }) {
	const { docId, label } = node.attrs;
	const handlers = extension.options.handlers;
	const [title, setTitle] = useState(label || 'Untitled');
	const [missing, setMissing] = useState(false);

	useEffect(() => {
		let cancelled = false;

		/* ABSENCE FROM THE LIST IS NOT PROOF OF DELETION. A page created moments
		   ago is legitimately missing from the subscription until the snapshot
		   lands, and rendering "(deleted)" in that window was both wrong and
		   alarming. So: found → use it; not found → keep the label and CONFIRM
		   with a direct read before ever claiming the page is gone. */
		const resolve = async () => {
			const resolved = handlers?.current?.resolveTitle?.(docId);
			if (cancelled) return;

			if (resolved !== undefined) {
				setMissing(false);
				setTitle(resolved || 'Untitled');
				return;
			}

			const verify = handlers?.current?.verifyExists;
			if (!verify) return; // can't confirm — stay optimistic
			const exists = await verify(docId);
			if (cancelled) return;
			setMissing(!exists);
		};

		resolve();
		// Re-resolve whenever the doc set changes, so renaming a child updates
		// every reference to it without a reload.
		const unsub = handlers?.current?.subscribe?.(() => resolve());
		return () => {
			cancelled = true;
			if (typeof unsub === 'function') unsub();
		};
	}, [docId, handlers]);

	const open = () => {
		if (missing) return;
		handlers?.current?.onOpen?.(docId);
	};

	/* Removing the BLOCK does not delete the DOCUMENT — the page stays in the
	   tree and is reachable from the list. Destroying a document by deleting a
	   line of text would be a far worse failure than leaving it one click away,
	   so the control says "Remove", not "Delete", and says so in its tooltip. */
	const remove = () => {
		if (typeof getPos !== 'function') return;
		const pos = getPos();
		editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
	};

	return (
		<NodeViewWrapper
			className={`cyv-childpage ${missing ? 'is-missing' : ''}`}
			data-drag-handle>
			<button
				type='button'
				className='cyv-childpage__btn'
				onClick={open}
				contentEditable={false}
				title={missing ? 'This page no longer exists' : `Open “${title}”`}>
				<span className='cyv-childpage__icon' aria-hidden='true'>
					<i className={`fa-solid fa-fw ${missing ? 'fa-file-slash' : 'fa-file-lines'}`}></i>
				</span>
				<span className='cyv-childpage__title'>
					{missing ? `${label || 'Untitled'} (deleted)` : title}
				</span>
				{!missing && (
					<span className='cyv-childpage__hint' aria-hidden='true'>
						<i className='fa-solid fa-arrow-right'></i>
					</span>
				)}
			</button>

			{/* Block controls. Without these the only way to remove a sub-page
			    reference was to select the block by keyboard and delete it —
			    undiscoverable, and the reason Anthony reported "no controls". */}
			<span className='cyv-childpage__controls' contentEditable={false}>
				{!missing && (
					<button
						type='button'
						title='Open in a new tab'
						aria-label={`Open ${title} in a new tab`}
						onClick={(e) => {
							e.stopPropagation();
							handlers?.current?.onOpenNewTab?.(docId);
						}}>
						<i className='fa-solid fa-fw fa-arrow-up-right-from-square'></i>
					</button>
				)}
				<button
					type='button'
					title='Remove this link (the page itself is kept)'
					aria-label='Remove this sub-page link'
					onClick={(e) => {
						e.stopPropagation();
						remove();
					}}>
					<i className='fa-solid fa-fw fa-xmark'></i>
				</button>
			</span>
		</NodeViewWrapper>
	);
}

const ChildPage = Node.create({
	name: 'childPage',
	group: 'block',
	atom: true,
	selectable: true,
	draggable: true,

	addOptions() {
		return { handlers: null };
	},

	addAttributes() {
		return {
			docId: {
				default: null,
				parseHTML: (el) => el.getAttribute('data-doc-id'),
				renderHTML: (attrs) => (attrs.docId ? { 'data-doc-id': attrs.docId } : {}),
			},
			label: {
				default: null,
				parseHTML: (el) => el.getAttribute('data-label'),
				renderHTML: (attrs) => (attrs.label ? { 'data-label': attrs.label } : {}),
			},
		};
	},

	parseHTML() {
		return [{ tag: 'div[data-child-page]' }];
	},

	renderHTML({ HTMLAttributes }) {
		return ['div', { ...HTMLAttributes, 'data-child-page': '', class: 'cyv-childpage' }];
	},

	/* Plain text for search/preview: without this the block contributes nothing
	   to plainText and a doc named only by its sub-pages is unfindable. */
	renderText({ node }) {
		return node.attrs.label || '';
	},

	addCommands() {
		return {
			/* Insert a reference to an EXISTING doc. */
			setChildPage:
				({ docId, label }) =>
				({ commands }) =>
					commands.insertContent({ type: this.name, attrs: { docId, label } }),

			/* Create a brand-new child document and drop a reference to it here.
			   Returns true immediately — the creation round-trip resolves after,
			   because a slash command cannot await. */
			insertNewChildPage:
				() =>
				({ editor }) => {
					const create = this.options.handlers?.current?.createChild;
					if (!create) return false;
					create()
						.then((child) => {
							if (!child?.id) return;
							editor
								.chain()
								.focus()
								.insertContent({
									type: this.name,
									attrs: { docId: child.id, label: child.title || 'Untitled' },
								})
								.run();
						})
						.catch((err) => console.error('[Docs] child page create failed', err));
					return true;
				},
		};
	},

	addNodeView() {
		return ReactNodeViewRenderer(ChildPageView);
	},
});

export default ChildPage;
